.strip {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: var(--gap);
}

.tile {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  min-height: var(--tile-min);
  padding: 12px 14px;
  transition:
    border-color var(--dur-1) var(--ease-std),
    background-color var(--dur-1) var(--ease-std);
}

/* Hover is a border change and a faint fill - no lift, no shadow (KD, 2026-11-01). */
.tile:hover {
  border-color: var(--accent-line);
  background: var(--panel-2);
}

.labelRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  /* The row is as tall as the info control, with or without one, so the figure in every tile of a
     strip starts on the same line. At 20px a tile with the control sat 4px lower than its neighbours. */
  min-height: var(--target-min);
}

.label {
  color: var(--muted);
  font-size: var(--t-min);
  font-weight: 600;
}

.figure {
  display: block;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  color: var(--ink);
  font: inherit;
  font-weight: 650;
  font-variant-numeric: lining-nums tabular-nums;
  line-height: var(--lh-24);
  text-align: left;
  /* **A figure is never cut** (design review, 2026-11-03: "47,216,402..." on Scope 2 at 1024). It
     shrinks with the window down to 18px, wraps at a space ("1,865 of" over "2,018") and puts its unit
     on the next line before it gives up a digit; only a single word that fits at no size breaks. */
  font-size: clamp(18px, 1.7vw, var(--t-figure));
  overflow-wrap: break-word;
  word-break: normal;
}

/* "No figure yet" is quiet, and a word is a state, not a total (ADR-0212; KD, 2026-11-02). */
.figure[data-kind="none"] {
  color: var(--muted);
  font-weight: 500;
}

.figure[data-kind="word"] {
  color: var(--ink-2);
  font-size: var(--t-head);
  font-weight: 600;
}

button.figure {
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: var(--line-2);
  text-underline-offset: 4px;
}

button.figure:hover {
  text-decoration-color: var(--accent);
}

button.figure:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
  border-radius: 4px;
}

.unit {
  display: inline-block;
  margin-left: 5px;
  /* Its own line height: inherited, the figure's 32px made the line box 36px beside a smaller unit and a
     tile with a unit sat 4px lower than its neighbours' links. */
  line-height: var(--lh-14);
  color: var(--muted);
  font-size: var(--t-dense);
  font-weight: 500;
}

/* Directly under the figure. It used to be pinned to the foot with a reserved three lines, which made
   Home's tiles 182px tall with 126px of them empty; a strip stretches its tiles to one height anyway. */
.link {
  font-size: var(--t-min);
}

/* The way to the rows behind a figure is the product's link colour, not the browser's blue. */
.link a:not([class]) {
  color: var(--accent);
  text-underline-offset: 3px;
}

/* The change since the comparison period, and the small line beside it. The tone tints the chip with
   the status pairs that are already measured; the figure above is never coloured. */
.trend {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 24px;
}

.delta {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 7px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-pill);
  background: var(--panel-2);
  color: var(--ink-2);
  font-size: var(--t-min);
  font-weight: 650;
  font-variant-numeric: lining-nums tabular-nums;
  white-space: nowrap;
}

.delta[data-tone="good"] {
  border-color: var(--ok-line);
  background: var(--ok-w);
  color: var(--ok);
}

.delta[data-tone="bad"] {
  border-color: var(--crit-line);
  background: var(--crit-w);
  color: var(--crit);
}

.against {
  color: var(--muted);
  font-weight: 500;
}

/* Grey: a hint at the shape, not a mark. The brand colour is chrome and never paints a figure's line. */
.spark {
  flex: 0 0 auto;
  color: var(--muted);
  overflow: visible;
}

.sparkEnd {
  fill: var(--ink-2);
}

/* The grey bar that stands where a figure will be while its read is out: as tall as the number it
   replaces (24px in the 32px line), so the tile does not change height when the answer lands. */
.figure .bone {
  width: 60%;
  height: 24px;
  margin: 4px 0;
  border-radius: var(--radius-ctl);
}

/* No draw-in on first paint: a tile's sparkline is part of a figure, and a figure does not animate
   when the screen opens (P13). */
